import React, { useState, useEffect } from 'react';
import { usePOSStore } from '../../../stores/pos.store';
import { Button } from '../../common/Button';
import { CreditCard, Banknote, Gift, SplitSquareHorizontal, CheckCircle2, MoreHorizontal, Loader2, AlertTriangle, Wallet, Clock, Tag } from 'lucide-react';
import { POSPaymentModal } from './POSPaymentModal';
import { usePaymentMethods, useBaseCurrency, useActiveRate, useBranches } from '../../../db/hooks/useLookupData';
import { useDatabase } from '../../../db/DatabaseProvider';
import { useAuthStore } from '../../../stores';
import { useToast } from '../../common/ToastProvider';
import { generateId, now } from '../../../lib/uuid';
import { formatLocal } from '../../../lib/currency';

interface POSPaymentStepProps {
  /** Callback to expose save transaction state to parent (POSDrawer) for sidebar action buttons */
  onSaveStateChange?: (state: {
    onPaid: (() => void) | null;
    onPending: (() => void) | null;
    isComplete: boolean;
    isSaving: boolean;
    lastTransactionId?: string | null;
  }) => void;
}

export function POSPaymentStep({ onSaveStateChange }: POSPaymentStepProps) {
  const {
    cartItems,
    paymentEntries,
    customer,
    setStep,
    removePaymentEntry,
    addPaymentEntry,
    discount,
    tip,
    tipOperatorId,
    setTip,
    operationalDate,
    includePreviousDebt,
    setIncludePreviousDebt,
    surplusMode,
    setSurplusMode,
    setDiscount,
  } = usePOSStore();
  const { paymentMethods = [] } = usePaymentMethods();
  const { branches = [] } = useBranches();
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  const { activeRate } = useActiveRate();
  const { selectedBranchId, user, profile } = useAuthStore();
  const db = useDatabase();
  const toast = useToast();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState<string | null>(null);

  const rateKey = baseCurrency.toLowerCase() as 'usd' | 'eur' | 'ves';
  const exchangeRateValue = rateKey === 'eur' ? (activeRate?.eur || 1) : rateKey === 'usd' ? (activeRate?.usd || 1) : 1;
  const toLocal = (val: number) => {
    if (rateKey === 'ves') return val;
    return Math.round((val * exchangeRateValue + Number.EPSILON) * 100) / 100;
  };

  const previousDebtAmount = customer && includePreviousDebt ? (customer.debt_balance || 0) : 0;
  const subtotal = cartItems.reduce((acc, item) => acc + Math.max(0, item.unitPrice - (item.discountAmount || 0)) * item.quantity, 0);
  const totalPaid = paymentEntries.reduce((acc, entry) => acc + entry.amount, 0);
  const netPayable = subtotal - discount + tip + previousDebtAmount;
  const pending = netPayable - totalPaid;
  const surplus = totalPaid > netPayable ? totalPaid - netPayable : 0;

  const handleSelectMethod = (method: any) => {
    setSelectedMethod(method);
    setIsModalOpen(true);
  };

  const isComplete = totalPaid >= netPayable && netPayable > 0;

  const getIconForMethod = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes('favor') || n.includes('saldo')) return Wallet;
    if (n.includes('efectivo')) return Banknote;
    if (n.includes('tarjeta') || n.includes('zelle')) return CreditCard;
    if (n.includes('regalo') || n.includes('gift')) return Gift;
    return MoreHorizontal;
  };

  const handleSaveTransaction = async (paymentStatus: 'paid' | 'pending') => {
    console.log('[DEBUG] handleSaveTransaction started', { paymentStatus });
    if (!db) {
      console.log('[DEBUG] No db available');
      return;
    }
    
    const branchId = selectedBranchId || profile?.assigned_branches?.[0] || branches[0]?.id;
    console.log('[DEBUG] branchId:', branchId);
    if (!branchId) {
      toast.error('Debe haber al menos una sucursal activa en la base de datos para registrar la venta.');
      return;
    }

    const hasPackages = cartItems.some((i) => i.type === 'package' || Boolean(i.package) || Boolean(i.selectedTier));
    if (hasPackages && !customer) {
      toast.error('La compra de paquetes o combos promocionales requiere asociar un cliente para acreditar sus sesiones.', 'Cliente Requerido');
      return;
    }

    const registrador = profile?.name || user?.email || 'Operador en Caja';
    const auditUser = user?.email || profile?.name || 'operador-desconocido';

    setIsSaving(true);
    try {
      console.log('[DEBUG] Inside try block');
      const transId = generateId('tx');
      const timestamp = now();
      const opFecha = operationalDate || timestamp;

      const description = `Venta POS - ${cartItems.length} ítem(s) para ${customer ? customer.name : 'Cliente Mostrador'}`;

      const items = cartItems.map((item) => {
        const isPackage = item.type === 'package' || Boolean(item.package);
        const subtotal = item.unitPrice * item.quantity;
        const discount = (item.discountAmount || 0) * item.quantity;
        const netAmount = Math.max(0, subtotal - discount);
        const serviceId = item.service?.id || null;
        const categoryId = item.service?.category_id || 'cat-general';
        const commissionPercentage = item.service?.commission_percentage || 0;
        const commissionAmount = (netAmount * commissionPercentage) / 100;
        const concept = isPackage ? (item.package?.name || 'Combo') : (item.service?.concept || 'Servicio');

        return {
          id: generateId('txi'),
          type: isPackage ? ('package' as const) : ('service' as const),
          concept,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          subtotal: subtotal,
          discount: discount,
          net_amount: netAmount,
          category_id: categoryId,
          service_id: serviceId,
          operator_id: item.operatorId || null,
          commission_percentage: commissionPercentage,
          commission_amount: commissionAmount,
        };
      });

      const payments = paymentEntries.map((p) => {
        const method = paymentMethods.find(m => m.id === p.paymentMethodId);
        const appliedRate = method?.currency === 'VES' ? exchangeRateValue : 1;
        return {
          id: generateId('txp'),
          payment_method_id: p.paymentMethodId,
          currency: method?.currency || baseCurrency,
          exchange_rate: appliedRate,
          received_amount: p.amount,
          base_equivalent: p.amount / appliedRate,
          reference: p.reference || null,
          bank_movement_id: null,
        };
      });

      await db.transactions.insert({
        id: transId,
        type: 'income',
        subtype: 'pos_sale',
        date: timestamp,
        operational_date: opFecha,
        period: opFecha.substring(0, 7),
        reference: null,
        description: description,
        total_amount: subtotal,
        paid_amount: netPayable - Math.max(0, pending),
        pending_balance: Math.max(0, pending),
        base_currency: baseCurrency,
        payment_status: paymentStatus === 'paid' ? 'paid' : 'pending',
        status: 'completed',
        void_reason: null,
        voided_by_user_id: null,
        branch_id: branchId,
        customer_id: customer?.id || null,
        customer_name: customer ? customer.name : 'Cliente Mostrador',
        notes: includePreviousDebt ? `Incluye cobro de deuda previa de ${currencySymbol}${(customer?.debt_balance || 0).toFixed(2)}` : null,
        reconciliation: null,
        items,
        payments,
        metadata: { 
          is_pos: true, 
          discount, 
          tip, 
          tip_operator_id: tipOperatorId || null, 
          exchange_rate_id: activeRate?.id || null, 
          surplus_mode: surplusMode, 
          surplus: surplus, 
        },
        registered_by: registrador,
        created_at: timestamp,
        updated_at: timestamp,
        is_deleted: false,
        _deleted: false,
        last_modified_by: auditUser,
      });

      // Update customer balance in RxDB
      if (customer && db.customers) {
        const customerDoc = await db.customers.findOne(customer.id).exec();
        if (customerDoc) {
          const currentDebt = customerDoc.debt_balance || 0;
          const currentCredit = customerDoc.credit_balance || 0;
          const creditUsed = paymentEntries
            .filter((e) => e.paymentMethodId === 'credit-balance')
            .reduce((acc, e) => acc + e.amount, 0);

          let newDebt = currentDebt;
          if (includePreviousDebt && paymentStatus === 'paid') {
            newDebt = 0;
          } else if (paymentStatus === 'pending') {
            newDebt = currentDebt + Math.max(0, pending);
          }

          let newCredit = Math.max(0, currentCredit - creditUsed);
          if (surplusMode === 'credit' && surplus > 0 && paymentStatus === 'paid') {
            newCredit = newCredit + surplus;
          }

          if (newDebt !== currentDebt || newCredit !== currentCredit) {
            await customerDoc.patch({
              debt_balance: newDebt,
              credit_balance: newCredit,
              updated_at: timestamp,
              last_modified_by: auditUser,
            });
          }
        }
      }

      if (customer && db.customer_packages) {
        for (const item of cartItems) {
          if (item.type === 'package' && item.package) {
            const pkg = item.package;
            const details = (pkg.items || []).map((it: any) => ({
              id: generateId('cpd'),
              service_id: it.service_id,
              total_sessions: it.session_count,
              used_sessions: 0,
              remaining_sessions: it.session_count,
            }));

            await db.customer_packages.insert({
              id: generateId('cpkg'),
              customer_id: customer.id,
              package_id: pkg.id,
              transaction_id: transId,
              package_name: pkg.name,
              purchase_date: timestamp,
              status: 'active',
              notes: `Adquirido vía POS`,
              details,
              attentions: [],
              created_at: timestamp,
              updated_at: timestamp,
              is_deleted: false,
            });
          } else if (item.selectedTier && item.service) {
            const esc = item.selectedTier;
            const details = [{
              id: generateId('cpd'),
              service_id: item.service?.id || '',
              total_sessions: esc.quantity,
              used_sessions: 0,
              remaining_sessions: esc.quantity,
            }];

            await db.customer_packages.insert({
              id: generateId('cpkg'),
              customer_id: customer.id,
              package_id: null,
              transaction_id: transId,
              package_name: `${item.service?.concept || 'Servicio'} (${esc.label})`,
              purchase_date: timestamp,
              status: 'active',
              notes: `Oferta volumen POS (${esc.label})`,
              details,
              attentions: [],
              created_at: timestamp,
              updated_at: timestamp,
              is_deleted: false,
            });
          }
        }
      }

      setLastTransactionId(transId);
      onSaveStateChange?.({
        onPaid: isComplete ? () => handleSaveTransaction('paid') : null,
        onPending: () => handleSaveTransaction('pending'),
        isComplete,
        isSaving: false,
        lastTransactionId: transId,
      });

      toast.success('Transacción registrada con éxito', 'Venta Completada');
      setStep('summary');
    } catch (err: any) {
      console.error('[DEBUG] Error guardando:', err);
      toast.error('Error guardando la transacción: ' + err.message, 'Fallo al Guardar');
    } finally {
      setIsSaving(false);
    }
  };

  // Expose save state to parent (POSDrawer) for sidebar action buttons
  useEffect(() => {
    onSaveStateChange?.({
      onPaid: isComplete ? () => handleSaveTransaction('paid') : null,
      onPending: () => handleSaveTransaction('pending'),
      isComplete,
      isSaving,
      lastTransactionId,
    });
  }, [isComplete, isSaving, paymentEntries.length, totalPaid, lastTransactionId, includePreviousDebt, surplusMode]);

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-3 sm:p-6 pb-24 lg:pb-6 flex flex-col animate-fade-in relative">
      
      {/* BCV Rate Badge Header */}
      {baseCurrency !== 'VES' && exchangeRateValue > 1 && (
        <div className="flex items-center justify-end mb-3">
          <div className="flex items-center gap-1.5 bg-brand-sky/10 border border-brand-sky/20 px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[11px] sm:text-xs font-extrabold text-brand-sky shrink-0">
            <span>Tasa BCV:</span>
            <span>1 {baseCurrency} = {formatLocal(exchangeRateValue)}</span>
          </div>
        </div>
      )}

      {/* Interactive Debt Panel */}
      {customer && (customer.debt_balance || 0) > 0 && (
        <div className="mb-3.5 bg-amber-500/10 border border-amber-500/30 rounded-2xl p-3 sm:p-4 flex items-center justify-between gap-3 animate-fade-in">
          <div className="flex items-center gap-2.5 min-w-0">
            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 shrink-0" />
            <div className="min-w-0">
              <span className="text-xs font-extrabold text-amber-700 dark:text-amber-400 block truncate">
                Deuda Pendiente: {currencySymbol}{(customer.debt_balance || 0).toFixed(2)}
              </span>
              <span className="text-[11px] text-text-secondary block truncate">
                {includePreviousDebt ? 'Deuda agregada al ticket actual' : '¿Deseas cobrar la deuda anterior ahora?'}
              </span>
            </div>
          </div>
          <Button
            size="xs"
            variant={includePreviousDebt ? 'secondary' : 'primary'}
            onClick={() => setIncludePreviousDebt(!includePreviousDebt)}
            className={includePreviousDebt ? 'border-amber-500 text-amber-500 font-bold shrink-0' : 'bg-amber-500 hover:bg-amber-600 border-none text-white font-extrabold shrink-0'}
          >
            {includePreviousDebt ? 'Remover Deuda' : '+ Incluir Deuda'}
          </Button>
        </div>
      )}

      {/* Usar Saldo a Favor Banner Button */}
      {customer && (customer.credit_balance || 0) > 0 && (
        <div className="mb-3 sm:mb-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-3 sm:p-4 flex items-center justify-between gap-2.5 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 text-emerald-700 dark:text-emerald-400 text-xs sm:text-sm font-semibold min-w-0">
            <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500 shrink-0" />
            <span className="truncate">
              Saldo a favor: {currencySymbol}{(customer.credit_balance || 0).toFixed(2)}
            </span>
          </div>
          <Button
            size="xs"
            disabled={isComplete || pending <= 0 || ((customer.credit_balance || 0) - paymentEntries.filter(e => e.paymentMethodId === 'credit-balance').reduce((s, e) => s + e.amount, 0)) <= 0}
            onClick={() => {
              const yaAplicado = paymentEntries
                .filter((e) => e.paymentMethodId === 'credit-balance')
                .reduce((s, e) => s + e.amount, 0);
              const disponible = (customer.credit_balance || 0) - yaAplicado;
              const amountToApply = Math.min(disponible, Math.max(0, pending));
              if (amountToApply > 0) {
                addPaymentEntry({
                  paymentMethodId: 'credit-balance',
                  amount: amountToApply,
                  reference: 'Canje de saldo a favor',
                });
              }
            }}
            className="bg-emerald-500 hover:bg-emerald-600 text-white border-none font-bold shrink-0"
          >
            Usar Saldo
          </Button>
        </div>
      )}
      
      {/* General Sale Discount Section (Subtle Inline Control) */}
      <div className="mb-4 bg-surface-secondary/70 border border-border p-2.5 sm:p-3 rounded-2xl flex items-center justify-between gap-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <Tag className="w-4 h-4 text-brand-orange shrink-0" />
          <span className="text-xs font-bold text-text-primary truncate">
            {discount > 0 ? `Descuento: -${currencySymbol}${discount.toFixed(2)}` : 'Descuento General'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-xs font-bold text-text-tertiary">{currencySymbol}</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={discount > 0 ? discount : ''}
            onChange={(e) => setDiscount(Math.max(0, parseFloat(e.target.value) || 0))}
            className="w-20 sm:w-24 px-2 py-1 rounded-xl border border-border bg-surface text-xs font-bold text-text-primary focus:border-brand-sky outline-none text-right"
          />
          {discount > 0 && (
            <button
              type="button"
              onClick={() => setDiscount(0)}
              className="text-[11px] font-extrabold text-error hover:underline cursor-pointer ml-1"
            >
              Quitar
            </button>
          )}
        </div>
      </div>

      {/* Decision Card: Vuelto (Cambio) vs Abono a Favor */}
      {surplus > 0 && (
        <div className="mb-4 bg-surface border border-border p-3.5 rounded-2xl space-y-2.5 animate-fade-in shadow-xs">
          <div className="flex items-center justify-between border-b border-border pb-2">
            <span className="text-xs font-extrabold text-text-primary">Sobrante de Pago:</span>
            <span className="text-sm font-black text-brand-orange">+{currencySymbol}{surplus.toFixed(2)}</span>
          </div>
          <p className="text-[11px] text-text-secondary">Selecciona qué hacer con los {currencySymbol}{surplus.toFixed(2)} sobrantes:</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setSurplusMode('change')}
              className={`p-2.5 rounded-xl border flex items-center justify-center gap-1.5 text-xs font-extrabold transition-all cursor-pointer ${
                surplusMode === 'change'
                  ? 'bg-brand-orange/15 border-brand-orange text-brand-orange ring-1 ring-brand-orange/30'
                  : 'bg-surface-secondary border-border text-text-secondary hover:bg-surface-tertiary'
              }`}
            >
              <Banknote className="w-4 h-4 shrink-0" />
              Entregar Vuelto
            </button>
            <button
              type="button"
              onClick={() => setSurplusMode('credit')}
              className={`p-2.5 rounded-xl border flex items-center justify-center gap-1.5 text-xs font-extrabold transition-all cursor-pointer ${
                surplusMode === 'credit'
                  ? 'bg-emerald-500/15 border-emerald-500 text-emerald-500 ring-1 ring-emerald-500/30'
                  : 'bg-surface-secondary border-border text-text-secondary hover:bg-surface-tertiary'
              }`}
            >
              <Wallet className="w-4 h-4 shrink-0" />
              Abonar a Cuenta
            </button>
          </div>
        </div>
      )}
      
      {/* Payment Methods Grid — 3 columns on mobile & desktop for compact alignment */}
      <div className="grid grid-cols-3 gap-2.5 sm:gap-4 mb-6">
        {paymentMethods.map((method) => {
          const Icon = getIconForMethod(method.name);
          return (
            <button
              key={method.id}
              onClick={() => handleSelectMethod(method)}
              disabled={isComplete}
              data-testid={`payment-method-${method.id}`}
              className={`
                flex flex-col items-center justify-center p-3 sm:p-5 rounded-xl sm:rounded-2xl border transition-all duration-200
                ${isComplete ? 'opacity-50 cursor-not-allowed bg-surface-tertiary border-border' : 'bg-surface border-border hover:border-brand-sky hover:shadow-xs hover:-translate-y-0.5 cursor-pointer'}
              `}
            >
              <Icon className={`w-5 h-5 sm:w-7 sm:h-7 mb-1.5 sm:mb-2 ${isComplete ? 'text-text-tertiary' : 'text-brand-sky'}`} />
              <span className="font-bold text-xs sm:text-sm text-text-primary text-center truncate max-w-full">{method.name}</span>
            </button>
          );
        })}

        {customer && (
          <button
            onClick={() => handleSaveTransaction('pending')}
            disabled={isSaving}
            className={`
              flex flex-col items-center justify-center p-3 sm:p-5 rounded-xl sm:rounded-2xl border transition-all duration-200 cursor-pointer
              bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20 hover:border-amber-500 hover:shadow-xs hover:-translate-y-0.5
              ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <Clock className="w-5 h-5 sm:w-7 sm:h-7 mb-1.5 sm:mb-2 text-amber-500" />
            <span className="font-bold text-xs sm:text-sm text-amber-700 dark:text-amber-400 text-center truncate max-w-full">Pago Crédito</span>
          </button>
        )}
        
        <button
          disabled={isComplete}
          onClick={() => {
            if (paymentMethods.length > 0) handleSelectMethod(paymentMethods[0]);
          }}
          className={`
            flex flex-col items-center justify-center p-3 sm:p-5 rounded-xl sm:rounded-2xl border border-dashed transition-all duration-200
            ${isComplete ? 'opacity-50 cursor-not-allowed bg-surface-tertiary border-border' : 'bg-surface border-border hover:border-brand-sky hover:text-brand-sky cursor-pointer'}
          `}
        >
          <SplitSquareHorizontal className="w-5 h-5 sm:w-7 sm:h-7 mb-1.5 sm:mb-2 text-text-tertiary" />
          <span className="font-bold text-xs sm:text-sm text-text-secondary text-center truncate max-w-full">Dividir Pago</span>
        </button>
      </div>

      {/* Registered Payments List */}
      {paymentEntries.length > 0 && (
        <div className="mt-auto">
          <h4 className="text-sm font-bold text-text-primary mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Pagos registrados
          </h4>
          <div className="space-y-2">
            {paymentEntries.map((entry) => {
              const method = paymentMethods.find(m => m.id === entry.paymentMethodId) || (
                entry.paymentMethodId === 'credit-balance'
                  ? { id: 'credit-balance', name: 'Saldo a favor', currency: baseCurrency }
                  : undefined
              );
              const isVESMethod = method?.currency === 'VES';
              const amountVES = entry.amount * exchangeRateValue;

              return (
                <div key={entry.id} className="flex items-center justify-between bg-surface border border-border p-3 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-surface-secondary rounded-full flex items-center justify-center">
                      <Banknote className="w-4 h-4 text-text-secondary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm block">{method?.name || entry.paymentMethodId}</span>
                        {isVESMethod && (
                          <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-brand-sky/10 text-brand-sky border border-brand-sky/20">
                            VES / Bs.S
                          </span>
                        )}
                      </div>
                      {entry.reference && <span className="text-xs text-text-tertiary">Ref: {entry.reference}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      {isVESMethod ? (
                        <>
                          <span className="font-extrabold text-sm block text-text-primary">{formatLocal(amountVES)}</span>
                          <span className="text-xs font-semibold text-text-tertiary block">({currencySymbol}{entry.amount.toFixed(2)})</span>
                        </>
                      ) : (
                        <span className="font-extrabold text-sm block text-text-primary">{currencySymbol}{entry.amount.toFixed(2)}</span>
                      )}
                    </div>
                    <Button variant="ghost" size="xs" onClick={() => removePaymentEntry(entry.id)}>X</Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isModalOpen && selectedMethod && (
        <POSPaymentModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          method={selectedMethod}
          pending={pending}
        />
      )}
    </div>
  );
}
